import PropTypes from 'prop-types';
import React, { Component } from 'react';

import _ from 'underscore';
import { titleize, unescapeHTML } from 'underscore.string';

import AccountContacts from 'components/v2/contacts';
import LabelValue from 'components/ui/tailwind/label-value';
import CargomaticCard from 'components/v2/card';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import LocationPreview from 'components/ui/tailwind/location-preview';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import PhoneField from 'components/v2/forms/PhoneField';
import { createPhoneNumberWithExtension } from 'utils/formatters/Phone';
import DefaultLocationForm from './default-location-form';
import Capability from '../Capability';
import EditButton from '../../../v2/buttons/EditButton';
import Container from '../../../v2/container';
import Sidebar from '../../shipper/Sidebar';
import { AvatarWithUpload } from '../AvatarWithUpload';

const REQUIRED_ATTRIBUTES = [
  'first_name',
  'last_name',
  'company_name',
  'email',
];

const getRequiredAsterisk = (field) => {
  return REQUIRED_ATTRIBUTES.includes(field) ? '*' : '';
};

class DefaultShipperDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: props.details,
      showModal: false,
      modalType: null,
      changed: {},
      errors: {
        first_name: {},
        last_name: {},
        phone: {},
        phone_extension: {},
        email: {},
        company_name: {},
        require_tracking_id: {},
      },
    };
  }

  errorsExist() {
    const { errors } = this.state;
    let errorsExist = false;
    _.map(errors, (value, key) => {
      if (!_.isEmpty(errors[key])) errorsExist = true;
    });
    return errorsExist;
  }

  onSave = () => {
    const { changed } = this.state;
    const { user } = this.state;
    const updates = _.pick(user, Object.keys(changed));
    updates._id = user._id;
    this.setState({ changed: {} }, () => {
      this.props.onSaveDetails('Shipper', updates);
    });
  };

  handleFieldChange = (field, value) => {
    const { t } = this.props;
    const { user, changed, errors } = this.state;

    user[field] = value;
    changed[field] = true;
    errors[field] =
      !value && REQUIRED_ATTRIBUTES.includes(field)
        ? {
            message: t('FIELD_REQUIRED_TIP', {
              field: titleize(t(sluggify(field))),
            }),
          }
        : {};

    this.setState({ user, changed, errors });
  };

  renderLocationEdit = (type) => {
    const {
      user: { origin, destination },
    } = this.state;
    const { onSaveOrigin, onSaveDestination, t } = this.props;
    const location = type === 'Origin' ? origin : destination;
    const onSave = type === 'Origin' ? onSaveOrigin : onSaveDestination;

    return (
      <DefaultLocationForm
        details={location}
        onSave={(data) => onSave(data)}
        title={t('DEFAULT_TYPE', { type })}
      />
    );
  };

  renderModal() {
    const {
      user: {
        first_name,
        last_name,
        company_name,
        phone,
        phone_extension,
        email,
        require_tracking_id,
      },
      modalType,
    } = this.state;
    const { errors } = this.state;
    const { t } = this.props;
    if (modalType === 'Origin' || modalType === 'Destination') {
      return this.renderLocationEdit(modalType);
    }

    return (
      <div>
        <div className="grid sm:grid-cols-6 sm:gap-x-4 sm:gap-y-6">
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.first_name}
            label={`${t('FIRST_NAME')} ${getRequiredAsterisk('first_name')}`}
            info={t('ACCOUNT_OWNER_FIRST_NAME')}
            onChange={(value) => this.handleFieldChange('first_name', value)}
            value={first_name}
          />
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.last_name}
            label={`${t('LAST_NAME')} ${getRequiredAsterisk('last_name')}`}
            info={t('ACCOUNT_OWNER_LAST_NAME')}
            onChange={(value) => this.handleFieldChange('last_name', value)}
            value={last_name}
          />
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.company_name}
            label={`${t('COMPANY_NAME')} ${getRequiredAsterisk(
              'company_name',
            )}`}
            info={t('ACCOUNT_COMPANY_NAME')}
            onChange={(value) => this.handleFieldChange('company_name', value)}
            value={unescapeHTML(company_name)}
            maxLength={75}
          />
          <CargomaticInput
            className="sm:col-span-4"
            error={errors.email}
            label={`${t('EMAIL')} ${getRequiredAsterisk('email')}`}
            info={t('SHIPPER_EMAIL')}
            onChange={(value) => this.handleFieldChange('email', value)}
            value={email}
            maxLength={254}
          />
          <div className="sm:col-span-3">
            <label className="form-label mb-1">{`${t(
              'PHONE_NUMBER',
            )} ${getRequiredAsterisk('phone')}`}</label>
            <PhoneField
              info={t('ACCOUNT_PRIMARY_PHONE_NUMBER')}
              onChange={(value) => this.handleFieldChange('phone', value)}
              value={phone}
            />
          </div>
          <CargomaticInput
            className="sm:col-span-3"
            error={errors.phone_extension}
            label={` ${titleize(t('EXT'))} ${getRequiredAsterisk('')}`}
            info={`${titleize(t('PHONE_NUMBER_EXTENSION_INFO'))}`}
            onChange={(value) => {
              this.handleFieldChange('phone_extension', value);
            }}
            value={phone_extension}
          />
          <CargomaticInput
            className="sm:col-span-full"
            type="checkbox"
            error={errors.require_tracking_id}
            label={`${t('REFERENCE_NUMBER_REQUIRED')} ${getRequiredAsterisk(
              'require_tracking_id',
            )}`}
            info={t('TRACKING_ID')}
            onChange={(value) => {
              this.handleFieldChange('require_tracking_id', value);
            }}
            value={require_tracking_id}
          />
        </div>
        <div className="mt-4 form-actions">
          <button
            className="button button-green"
            onClick={this.onSave}
            disabled={this.errorsExist()}
          >
            {titleize(t('SAVE'))}
          </button>
        </div>
      </div>
    );
  }

  handleLocationRemove(field) {
    const updates = {
      [field]: null,
      _id: this.state.user._id,
    };
    this.props.onSaveDetails('Shipper', updates);
  }

  renderFromAddress = () => {
    if (
      this.state.user?.from_address_is_verified &&
      this.state.user?.from_address
    ) {
      const { t } = this.props;
      return (
        <div className="flex justify-between mb-2 -mx-4">
          <div className="w-1/2 px-4">
            <span className="form-label">{titleize(t('FROM_ADDRESS'))}</span>
          </div>
          <div className="px-4">
            <span className="text-gray-500">
              {this.props.details.from_address}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  render() {
    const {
      first_name,
      last_name,
      company_name,
      email,
      require_tracking_id,
      phone,
      phone_extension,
    } = this.state.user;
    const {
      details: { origin, destination, _id },
      t,
    } = this.props;

    return (
      <Container>
        <div className="lg:grid lg:grid-cols-4 lg:gap-6 mt-10">
          <div className="mb-6 lg:col-span-1">
            <Sidebar activePage="profile" />
          </div>
          <div className="lg:col-span-1">
            <CargomaticCard className="mb-6">
              <div className="cursor-pointer">
                <div className="flex justify-center">
                  <AvatarWithUpload
                    key={_id}
                    userId={_id}
                    style={{
                      display: 'inline-block',
                      padding: 2,
                      textAlign: 'center',
                    }}
                    avatarClassName="h-32 w-32 cursor-pointer rounded-full"
                  />
                </div>
              </div>
              <h2 className="text-center text-grey-darker">{`${unescapeHTML(
                company_name,
              )}`}</h2>
              <hr />
              <header
                className="flex justify-between cursor-pointer"
                onClick={() => {
                  this.setState({ showModal: true, modalType: 'account' });
                }}
              >
                <h3 className="my-0">{t('ACCOUNT')}</h3>
                <EditButton />
              </header>
              <LabelValue value={`${unescapeHTML(company_name)}`} icon="home" />
              <LabelValue value={`${first_name} ${last_name}`} icon="user" />
              <LabelValue
                value={createPhoneNumberWithExtension(
                  phone,
                  phone_extension,
                  t('EXT'),
                )}
                icon="phone"
              />
              <LabelValue value={`${email}`} icon="envelope" />
            </CargomaticCard>
            <CargomaticCard
              className="mb-6"
              header={() => (
                <header
                  className="flex items-center justify-between"
                  onClick={() => {
                    this.setState({ showModal: true, modalType: 'account' });
                  }}
                >
                  <h3 className="my-0">{t('SETTINGS')}</h3>
                  <EditButton />
                </header>
              )}
            >
              <Capability
                className="mb-6"
                label={t('REQUIRES_TRACKING')}
                checked={require_tracking_id}
              />
              {this.renderFromAddress()}
            </CargomaticCard>
            <div className="pt-6 border-t border-solid border-grey-light">
              <button
                onClick={() => this.props.onOpenPassword()}
                className="w-full button button-hollow-red"
              >
                {t('CHANGE_PASSWORD')}
              </button>
            </div>
          </div>
          <div className="col-span-2">
            <AccountContacts
              className="mb-6"
              contacts={this.state.user.contacts}
              userId={this.state.user._id}
            />
            <div className="mb-6">
              <LocationPreview
                onRemove={() => this.handleLocationRemove('origin')}
                type="Origin"
                location={origin}
                onEdit={() => {
                  this.setState({ showModal: true, modalType: 'Origin' });
                }}
              />
            </div>
            <div className="mb-6">
              <LocationPreview
                onRemove={() => this.handleLocationRemove('destination')}
                type="Destination"
                location={destination}
                onEdit={() => {
                  this.setState({ showModal: true, modalType: 'Destination' });
                }}
              />
            </div>
          </div>

          {this.state.showModal && (
            <CargomaticModal
              show
              onHide={() => {
                this.setState({ showModal: false, modalType: null });
              }}
              title={t('EDIT_ACCOUNT')}
              body={this.renderModal()}
              footer={null}
              location="middle"
            />
          )}
        </div>
      </Container>
    );
  }
}

DefaultShipperDetails.propTypes = {
  details: PropTypes.object.isRequired,
  onOpenPassword: PropTypes.func.isRequired,
  onSaveDetails: PropTypes.func.isRequired,
  onSaveOrigin: PropTypes.func.isRequired,
  onSaveDestination: PropTypes.func.isRequired,
};

export default withTranslation()(DefaultShipperDetails);
